// src/services/userImpersonationService.js
import apiService from '../utils/apiInterceptor';
import { notification } from 'antd';
import config from '../config';
import userRolePermissionService from './userRolePermissionService';

// Keys for storing impersonation data
const IMPERSONATION_TOKEN_KEY = '_admin_token';
const IMPERSONATION_USER_KEY = '_admin_user';
const IMPERSONATION_ROLES_KEY = '_admin_roles';
const IMPERSONATION_ROLES_FULL_KEY = '_admin_roles_full';
const IMPERSONATION_PERMISSIONS_KEY = '_admin_permissions';
const IMPERSONATION_PERMISSIONS_FULL_KEY = '_admin_permissions_full';
const IMPERSONATION_FLAG_KEY = 'is_impersonating';
const IMPERSONATION_RETURN_PATH_KEY = '_admin_return_path';

const userImpersonationService = {
  /**
   * Start impersonation - returns new user token while saving admin session
   * @param {number|string} userId - User ID to impersonate
   * @returns {Promise<Object>} Result of impersonation attempt
   */
  impersonateUser: async (userId) => {
    try {
      // Store the current path for returning later
      localStorage.setItem(IMPERSONATION_RETURN_PATH_KEY, window.location.pathname);
      
      // Store the admin's current token, user, roles, and permissions
      localStorage.setItem(IMPERSONATION_TOKEN_KEY, localStorage.getItem(config.AUTH_TOKEN_KEY));
      localStorage.setItem(IMPERSONATION_USER_KEY, localStorage.getItem('user'));
      localStorage.setItem(IMPERSONATION_ROLES_KEY, localStorage.getItem('user_roles') || '[]');
      localStorage.setItem(IMPERSONATION_ROLES_FULL_KEY, localStorage.getItem('user_roles_full') || '[]');
      localStorage.setItem(IMPERSONATION_PERMISSIONS_KEY, localStorage.getItem('user_permissions') || '[]');
      localStorage.setItem(IMPERSONATION_PERMISSIONS_FULL_KEY, localStorage.getItem('user_permissions_full') || '[]');
      
      // Make the impersonation API call
      const response = await apiService.post('/admin/impersonate', { user_id: userId });
      console.log('Impersonation response:', response);
      
      if (response && response.token) {
        // Set the impersonated user's token and data
        localStorage.setItem(config.AUTH_TOKEN_KEY, response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        localStorage.setItem(IMPERSONATION_FLAG_KEY, 'true');
        
        // Load impersonated user's roles and permissions
        if (response.user && response.user.id) {
          try {
            // Get user roles
            const rolesResponse = await userRolePermissionService.getUserRoles(response.user.id);
            if (rolesResponse.data && rolesResponse.data.roles) {
              const roleNames = rolesResponse.data.roles.map(role => role.name);
              localStorage.setItem('user_roles', JSON.stringify(roleNames));
            } else {
              localStorage.setItem('user_roles', '[]');
              localStorage.setItem('user_roles_full', '[]');
            }
            
            // Get user permissions
            await userRolePermissionService.loadUserPermissions(response.user.id);
            
            // Dispatch an event to notify the app about permission changes
            window.dispatchEvent(new Event('permissionsChanged'));
          } catch (error) {
            console.error('Error loading impersonated user roles and permissions:', error);
            localStorage.setItem('user_roles', '[]');
            localStorage.setItem('user_roles_full', '[]');
            localStorage.setItem('user_permissions', '[]');
            localStorage.setItem('user_permissions_full', '[]');
          }
        }
        
        return { success: true, message: 'Impersonation started successfully', user: response.user };
      }
      
      return { success: false, message: 'Failed to impersonate user: No token received' };
    } catch (error) {
      console.error('Impersonation error:', error);
      return { 
        success: false, 
        message: error.response?.message || 'Failed to impersonate user' 
      };
    }
  },
  
  /**
   * End impersonation - restore admin session
   * @returns {Object} Result of ending impersonation
   */
  stopImpersonation: () => {
    try {
      // Check if we're currently impersonating
      const isImpersonating = localStorage.getItem(IMPERSONATION_FLAG_KEY) === 'true';
      
      if (!isImpersonating) {
        return { success: false, message: 'Not currently impersonating any user' };
      }
      
      // Get the return path if available
      const returnPath = localStorage.getItem(IMPERSONATION_RETURN_PATH_KEY) || '/admin/dashboard';
      
      // Restore the admin token and user data
      const adminToken = localStorage.getItem(IMPERSONATION_TOKEN_KEY);
      const adminUser = localStorage.getItem(IMPERSONATION_USER_KEY);
      const adminRoles = localStorage.getItem(IMPERSONATION_ROLES_KEY) || '[]';
      const adminRolesFull = localStorage.getItem(IMPERSONATION_ROLES_FULL_KEY) || '[]';
      const adminPermissions = localStorage.getItem(IMPERSONATION_PERMISSIONS_KEY) || '[]';
      const adminPermissionsFull = localStorage.getItem(IMPERSONATION_PERMISSIONS_FULL_KEY) || '[]';
      
      // Make sure we have valid admin data before continuing
      if (!adminToken || !adminUser) {
        console.error('Missing admin credentials for ending impersonation');
        notification.error({
          message: 'Error',
          description: 'Could not restore admin session. Please log in again.'
        });
        
        // Force logout and redirect to login
        localStorage.clear();
        window.location.href = '/login';
        return { success: false, message: 'Missing admin credentials' };
      }
      
      // Restore admin session
      localStorage.setItem(config.AUTH_TOKEN_KEY, adminToken);
      localStorage.setItem('user', adminUser);
      localStorage.setItem('user_roles', adminRoles);
      localStorage.setItem('user_roles_full', adminRolesFull);
      localStorage.setItem('user_permissions', adminPermissions);
      localStorage.setItem('user_permissions_full', adminPermissionsFull);
      
      // Clean up impersonation data
      localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
      localStorage.removeItem(IMPERSONATION_USER_KEY);
      localStorage.removeItem(IMPERSONATION_ROLES_KEY);
      localStorage.removeItem(IMPERSONATION_ROLES_FULL_KEY);
      localStorage.removeItem(IMPERSONATION_PERMISSIONS_KEY);
      localStorage.removeItem(IMPERSONATION_PERMISSIONS_FULL_KEY);
      localStorage.removeItem(IMPERSONATION_FLAG_KEY);
      localStorage.removeItem(IMPERSONATION_RETURN_PATH_KEY);
      
      // Dispatch an event to notify the app about permission changes
      window.dispatchEvent(new Event('permissionsChanged'));
      
      return { 
        success: true, 
        message: 'Impersonation ended successfully',
        returnPath 
      };
    } catch (error) {
      console.error('Error ending impersonation:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to end impersonation properly. The page will reload to restore your session.'
      });
      
      // Force cleanup and reload if there's an error
      localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
      localStorage.removeItem(IMPERSONATION_USER_KEY);
      localStorage.removeItem(IMPERSONATION_ROLES_KEY);
      localStorage.removeItem(IMPERSONATION_ROLES_FULL_KEY);
      localStorage.removeItem(IMPERSONATION_PERMISSIONS_KEY);
      localStorage.removeItem(IMPERSONATION_PERMISSIONS_FULL_KEY);
      localStorage.removeItem(IMPERSONATION_FLAG_KEY);
      localStorage.removeItem(IMPERSONATION_RETURN_PATH_KEY);
      
      return { success: false, message: 'Failed to end impersonation cleanly' };
    }
  },
  
  /**
   * Check if currently impersonating
   * @returns {boolean} True if currently impersonating
   */
  isImpersonating: () => {
    return localStorage.getItem(IMPERSONATION_FLAG_KEY) === 'true';
  },
  
  /**
   * Get impersonated user info
   * @returns {Object|null} Impersonated user data or null
   */
  getImpersonatedUser: () => {
    if (!userImpersonationService.isImpersonating()) {
      return null;
    }
    
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch (e) {
      console.error('Error parsing impersonated user data:', e);
      return null;
    }
  },
  
  /**
   * Get original admin user info
   * @returns {Object|null} Admin user data or null
   */
  getAdminUser: () => {
    if (!userImpersonationService.isImpersonating()) {
      return null;
    }
    
    try {
      return JSON.parse(localStorage.getItem(IMPERSONATION_USER_KEY));
    } catch (e) {
      console.error('Error parsing admin user data:', e);
      return null;
    }
  },
  
  /**
   * Get original admin permissions
   * @returns {Array} Admin permissions array
   */
  getAdminPermissions: () => {
    if (!userImpersonationService.isImpersonating()) {
      return [];
    }
    
    try {
      return JSON.parse(localStorage.getItem(IMPERSONATION_PERMISSIONS_KEY) || '[]');
    } catch (e) {
      console.error('Error parsing admin permissions data:', e);
      return [];
    }
  },
  
  /**
   * Check if admin had a specific permission
   * @param {string} permissionName - Permission to check
   * @returns {boolean} True if admin had the permission
   */
  adminHadPermission: (permissionName) => {
    if (!userImpersonationService.isImpersonating()) {
      return false;
    }
    
    const adminPermissions = userImpersonationService.getAdminPermissions();
    return adminPermissions.includes(permissionName);
  }
};

export default userImpersonationService;
